import { useState, useRef } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useTranslation } from 'react-i18next';
import { Modal, ModalSize } from "@components/new/Modal";
import { Button, ButtonSize, ButtonType } from "@components/new/Button";
import Stepper, { EStepState } from "@components/new/Stepper";
import { MessageModalContent } from '@components/modals/MessageModalNew';
import {
  getFBPRequestDetails,
  TProductDimensionLimits,
} from "@api/apiClient/fbp";
import Form from "./Form";
import { FormHandle } from "./types";
import styles from "./CreateOrderModal.module.scss";

interface OrderModalProps {
  onClose: () => void;
  sellerWarehouseId: string;
  fbpWarehouseId: string;
  isPickUpAvailable: boolean;
  openPickUp: (v: string) => void;
  wbDimensionLimits?: TProductDimensionLimits;
  aliDimensionLimits?: TProductDimensionLimits;
  ymDimensionLimits?: TProductDimensionLimits;
  wbDensityLimits?: number;
  aliDensityLimits?: number;
  ymDensityLimits?: number;
}

interface ISubmitButtonProps {
  disabled: boolean;
  isLoading?: boolean;
  pickUpAvailable?: boolean;
}

enum steps {
  FORM = "form",
  SUCCESS = "success",
  ERROR = "error",
}

const CreateOrderModal = ({
  onClose,
  sellerWarehouseId,
  fbpWarehouseId,
  isPickUpAvailable,
  openPickUp,
  wbDimensionLimits,
  aliDimensionLimits,
  ymDimensionLimits,
  wbDensityLimits,
  aliDensityLimits,
  ymDensityLimits,
}: OrderModalProps) => {
  const [step, setStep] = useState(steps.FORM);
  const [requestId, setRequestId] = useState("");
  const [ buttonProps, setButtonProps ] = useState<ISubmitButtonProps>({
    disabled: false,
    isLoading: false,
    pickUpAvailable: false,
  });
  const [ headerMessage, setHeaderMessage ] = useState<React.ReactNode>();
  const [accordenPage, setAccordeonPage] = useState<string>("items");
  const { t } = useTranslation();
  const formRef = useRef<FormHandle>({ submit: () => {} });
  const queryClient = useQueryClient();

  const requestQuery = useQuery({
    queryKey: ["getFBPRequestDetails", requestId],
    queryFn: () => getFBPRequestDetails(requestId),
    retry: 1,
    enabled: Boolean(requestId),
    onSuccess: () => {
      setStep(steps.SUCCESS);
    }
  });

  const handleStatusChange = (props: ISubmitButtonProps, msg: React.ReactNode) => {
    setButtonProps({
      disabled: props.disabled ?? false,
      isLoading: props.isLoading ?? false,
      pickUpAvailable: props.pickUpAvailable,
    });
    setHeaderMessage(msg);
  }

  const onSuccess = (newRequestId: string) => {
    queryClient.invalidateQueries(
      { queryKey: ["getFBPRequests"] },
    );
    setRequestId(newRequestId);
    // setStep(steps.SUCCESS);
  }

  const onError = () => {
    setStep(steps.ERROR);
  }

  const stepsData = [
    { text: t('fbpPage.createOrder.placing.step-1'), state: EStepState.ENABLED },
    { text: t('fbpPage.createOrder.placing.step-2'), state: (step === steps.FORM && accordenPage === "items") ? EStepState.DISABLED : EStepState.ENABLED},
    {
      text: t('fbpPage.createOrder.placing.step-3'),
      state: (step === steps.SUCCESS ? EStepState.SUCCESS : (
        step === steps.ERROR ? EStepState.ERROR : EStepState.DISABLED
      )),
    },
  ]

  if (step === steps.SUCCESS) {
    return (
      <Modal
        isOpen={true}
        title={t('fbpPage.createOrder.title')}
        headerContent={<Stepper steps={stepsData} />}
        size={ModalSize.L}
        onClose={onClose}
        footerButtons={[
          {
            title: t('fbpPage.createOrder.cancelBtn'),
            onClick: onClose,
          },
        ]}
      >
        <MessageModalContent
          className={styles.successContent}
          descriptionClassName={styles.successDescription}
          title={t('fbpPage.createOrder.completion.successTitle')}
          description={t(
            (isPickUpAvailable && buttonProps.pickUpAvailable && requestQuery.data?.isPickUpAvailable) ?
              'fbpPage.createOrder.completion.successDescriptionPickUp' :
              'fbpPage.createOrder.completion.successDescription'
          )}
          footer={(isPickUpAvailable && buttonProps.pickUpAvailable && requestQuery.data?.isPickUpAvailable) ? (
            <Button
              className={styles.successButton}
              type={ButtonType.PRIMARY}
              onClick={() => {
                onClose();
                openPickUp(requestId);
              }}
              title={t('fbpPage.createOrder.completion.successBtn')}
              size={ButtonSize.L}
            />
          ) : null}
          variant="success"
        />
      </Modal>
    )
  }

  if (step === steps.ERROR) {
    return (
      <Modal
        isOpen={true}
        title={t('fbpPage.createOrder.title')}
        headerContent={<Stepper steps={stepsData}/>}
        size={ModalSize.L}
        onClose={onClose}
        footerButtons={[
          {
            title: t('fbpPage.createOrder.cancelBtn'),
            onClick: onClose,
          },
          {
            title: t('fbpPage.createOrder.completion.repeatBtn'),
            onClick: () => setStep(steps.FORM),
          },
        ]}
      >
        <MessageModalContent
          title={t('fbpPage.createOrder.completion.errorTitle')}
          description={t('fbpPage.createOrder.completion.errorDescription')}
          variant="warning"
        />
      </Modal>
    )
  }

  return (
    <Modal
      isOpen={true}
      title={t('fbpPage.createOrder.title')}
      headerContent={<Stepper steps={stepsData} />}
      headerTitleContent={headerMessage}
      size={ModalSize.L}
      onClose={onClose}
      confirmCancel
      confirmCancelDescription={t('fbpPage.createOrder.closeConfirmDescription')}
      footerButtons={[
        {
          title: t('fbpPage.createOrder.cancelBtn'),
        },
        {
          title: t('fbpPage.createOrder.placing.saveBtn'),
          onClick: () => {
            formRef.current && formRef.current.submit();
          },
          disabled: buttonProps.disabled,
          loading: requestQuery.isLoading || buttonProps.isLoading,
        },
      ]}
    >
      <Form
        formRef={formRef}
        sellerWarehouseId={sellerWarehouseId}
        fbpWarehouseId={fbpWarehouseId}
        isPickUpAvailable={isPickUpAvailable}
        wbDimensionLimits={wbDimensionLimits}
        aliDimensionLimits={aliDimensionLimits}
        ymDimensionLimits={ymDimensionLimits}
        wbDensityLimits={wbDensityLimits}
        aliDensityLimits={aliDensityLimits}
        ymDensityLimits={ymDensityLimits}
        onStatusChange={handleStatusChange}
        onSuccess={onSuccess}
        onError={onError}
        setAccordeonPage={setAccordeonPage}
      />
    </Modal>
  )
}

export default CreateOrderModal;
